import * as Api from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	useCancelAllInProgressAutomationsMutation,
	useAutomationTemplateSetStatusMutation,
} from '../../../../queries/Automation';
import { IModalContext } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { WarningIcon } from '../../svgs/icons/WarningIcon';

interface IProps extends IModalContext {
	automationTemplate: Api.IAutomationTemplateReference;
	impersonationContext?: Api.IImpersonationContext;
	className?: string;
	isOpen?: boolean;
	onRequestClose?(result: AutomationDisableCancelSelection): void;
	styles?: StyleDeclarationValue[];
}

export enum AutomationDisableCancelSelection {
	Disable,
	DisableAndCancelAllInProgress,
	DoNothing,
}

export const AutomationDisableCancelModal: React.FC<IProps> = props => {
	const { automationTemplate, impersonationContext, isOpen, onRequestClose } = props;
	const { logEvent, logApiError } = useEventLogging('AutomationDisableCancelModal');
	const errorMessages = useErrorMessages();

	const updateStatusMutation = useAutomationTemplateSetStatusMutation({
		onSuccess: () => {
			// Success handled by callback
		},
		onError: error => {
			logApiError('DisableAutomation-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const cancelAllInProgressMutation = useCancelAllInProgressAutomationsMutation({
		onSuccess: () => {
			// Success handled by callback
		},
		onError: error => {
			logApiError('CancelAllInProgress-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const options = React.useMemo(() => {
		if (automationTemplate) {
			const disableConfirmationOptions: IConfirmationDialogOption<AutomationDisableCancelSelection>[] =
				(automationTemplate.stats?.inProgress ?? 0) > 0
					? [
							{
								isDestructive: true,
								representedObject: AutomationDisableCancelSelection.DisableAndCancelAllInProgress,
								title: 'Disable and cancel all in progress',
							},
							{
								isCta: true,
								representedObject: AutomationDisableCancelSelection.Disable,
								title: 'Disable automation only',
							},
							{
								isCancel: true,
								representedObject: AutomationDisableCancelSelection.DoNothing,
								title: 'Do not disable',
							},
						]
					: [
							{
								isCta: true,
								representedObject: AutomationDisableCancelSelection.Disable,
								title: 'Yes, disable automation',
							},
							{
								isCancel: true,
								representedObject: AutomationDisableCancelSelection.DoNothing,
								title: 'Do not disable',
							},
						];
			return disableConfirmationOptions;
		}
		return [];
	}, [automationTemplate]);

	const onDisableConfirmationDialogRequestClose = React.useCallback(
		async (result?: IConfirmationDialogOption<AutomationDisableCancelSelection>) => {
			if (
				result?.representedObject === AutomationDisableCancelSelection.Disable ||
				result?.representedObject === AutomationDisableCancelSelection.DisableAndCancelAllInProgress
			) {
				try {
					if (result.representedObject === AutomationDisableCancelSelection.DisableAndCancelAllInProgress) {
						logEvent('CancelAllInProgress');
						await cancelAllInProgressMutation.mutateAsync({
							id: automationTemplate.id,
							impersonationContext,
						});
					}

					logEvent('DisableAutomation', { templateId: automationTemplate.id });
					await updateStatusMutation.mutateAsync({
						id: automationTemplate.id,
						status: Api.TemplateStatus.Disabled,
						impersonationContext,
					});

					const message = result.isDestructive
						? 'Automation template disabled and in progress cancelled.'
						: 'Automation template disabled.';
					toast.success(message);
				} catch {
					// Error already handled by mutation error callbacks
				}
			}

			onRequestClose?.(result?.representedObject);
		},
		[
			onRequestClose,
			automationTemplate.id,
			impersonationContext,
			cancelAllInProgressMutation,
			updateStatusMutation,
			logEvent,
		]
	);

	return (
		<ConfirmationDialog
			icon={<WarningIcon />}
			modalProps={{
				isOpen,
				onRequestClose: onDisableConfirmationDialogRequestClose,
			}}
			options={options}
			stackButtonsVertically={true}
			title={<span>Disabling automation</span>}
		>
			{(automationTemplate.stats?.inProgress ?? 0) > 0 && (
				<div style={{ textAlign: 'center' }}>
					Contacts that have existing automation in progress
					<br />
					can be cancelled or left to run.
				</div>
			)}
		</ConfirmationDialog>
	);
};
